import { EventsFilterComponent, EventsFilterOutput } from '../../core/components/table/filter-panel/filters/events-filter/events-filter.component';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SortComponent } from '../../core/components/sort/sort.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { ToastrService } from 'ngx-toastr';
import { Client, EventDataChannel, EventDataOut, EventQueryDataIn, PageInfo, SortingType } from '../../../api/api-reference';
import { ClipboardService } from '../../../services/clipboard.service';
import { DateRangeInput } from '../../core/models/date-range-input';
import { getAlertColor } from '../alert-colors';
import { Column } from '../../core/models/column';
import { processDateRange } from '../../../helper/date-utils';

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SortComponent,
    SearchComponent,
    DatepickerComponent,
    PaginationComponent,
    EventsFilterComponent,
    IsNewDirective
  ],
  templateUrl: './events.component.html',
  styleUrl: './events.component.scss'
})
export class EventsComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfEvents: number = 0;
  public lastPageSize: number = 1;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  selectedColumns: Column[] = [];
  filtersApplied = 0;
  selectedFilters: SelectedFilters = {
    assetTypes: [],
    channelIds: [],
  };
  

  data: EventDataOut[] | undefined = [];
  getAlertColor = getAlertColor;
  requestData = new EventQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
    channelIds: [],
  });
  constructor(
    private toastr: ToastrService,
    private client: Client,
    public clipboardService: ClipboardService,
    private router: Router,
  ) {
    const navState = this.router.lastSuccessfulNavigation?.extras?.state;
    if (navState?.['channelIds']?.length) {
      const channelIds: EventDataChannel[] = navState['channelIds'].map(
        (name: string) => EventDataChannel[name as keyof typeof EventDataChannel]
      ).filter(Boolean);
      this.requestData.channelIds = channelIds;
      this.selectedFilters.channelIds = navState['channelIds'];
    }
    this.reloadTable(1);
  }
  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.client.query2(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfEvents = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfEvents / this.pageSize);
        this.filtersApplied =
          (this.requestData.assetTypes?.length || 0) +
          (this.requestData.channelIds?.length || 0);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }
  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.query2(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfEvents = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfEvents / this.pageSize);
        this.filtersApplied = this.requestData.assetTypes?.length || 0;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  updateColumns(columns: Column[]) {
    this.selectedColumns = columns;
  }
  onDataReceived(event: EventsFilterOutput) {
    this.selectedFilters = {
      assetTypes: event.selectedAssetTypes,
      channelIds: event.selectedEventChannels
    };
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.requestData.channelIds = event.selectedEventChannels.map(
      (statusStr: string) => {
        return EventDataChannel[
          statusStr as keyof typeof EventDataChannel
        ];
      },
    );
    this.reloadTable();
  }
}
export interface SelectedFilters {
  assetTypes: string[];
  channelIds?: string[];
}
